import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';
import KetLetterInput from './KetLetterInput';
import { ketNumberMap } from './ketNumbering';

// Shared A–H letter picker used by the KET matching parts
// (Reading & Writing Part 1 / 3b, Listening Part 2).
export const KetLetterPicker = ({ question, letters, answer, onSelect, showHint, isReviewMode, isHydrated }) => {
  const isExample = question.points === 0;
  const isAnswerCorrect = question.is_correct === true || question.is_correct === 'true';
  const correctAnswer = question.accepted_answers && question.accepted_answers[0];

  const selectedValue = isExample
    ? correctAnswer
    : (showHint || (isReviewMode && isAnswerCorrect))
      ? correctAnswer
      : answer;

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {letters.map(letter => {
        const isSelected = selectedValue === letter;
        const isThisCorrect = question.accepted_answers?.includes(letter);
        const isIncorrect = isReviewMode && !isAnswerCorrect && answer === letter && !isThisCorrect;

        return (
          <span
            key={letter}
            onClick={() => !isReviewMode && !showHint && !isExample && isHydrated && onSelect(question.id, letter)}
            className={`w-8 h-8 rounded-full border-2 flex items-center justify-center shrink-0 text-sm font-black transition-all select-none
              ${isExample || isReviewMode || showHint ? 'cursor-default' : 'cursor-pointer'}
              ${isSelected && !isReviewMode && !showHint && !isExample ? 'bg-primary-blue border-primary-blue text-white' : ''}
              ${isSelected && (showHint || isExample) && !isReviewMode ? 'bg-amber-500 border-amber-500 text-white' : ''}
              ${isReviewMode && isThisCorrect ? 'bg-green-500 border-green-500 text-white' : ''}
              ${isIncorrect ? 'bg-red-400 border-red-400 text-white' : ''}
              ${!isSelected && !isIncorrect && !(isReviewMode && isThisCorrect) ? 'border-slate-200 dark:border-slate-600 text-slate-400 hover:border-blue-300' : ''}
            `}
          >
            {letter}
          </span>
        );
      })}
      {isReviewMode && !isExample && (isAnswerCorrect
        ? <span className="correct-checkmark">✓</span>
        : <span className="incorrect-checkmark">✗</span>
      )}
      {isReviewMode && !isExample && <QuestionLearnButton question={question} />}
    </div>
  );
};

const SentenceRow = ({ question, questionNumber, letters, answer, onSelect, showHint, isReviewMode, isHydrated }) => {
  const isExample = question.points === 0;
  const sentence = question.question_text.replace(/\(Example\)/gi, '').trim();

  return (
    <div className={`p-4 rounded-2xl border-2 transition-all flex flex-col md:flex-row md:items-center gap-3 ${isExample ? 'bg-blue-50/50 border-blue-100' : 'bg-white border-slate-100 dark:bg-slate-800/50 dark:border-slate-700 hover:border-blue-200 shadow-sm'}`}>
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <span className={`shrink-0 font-black px-2.5 py-1 rounded-xl text-sm border shadow-sm ${isExample ? 'text-white bg-amber-500 border-amber-500' : 'text-slate-400 bg-slate-50 dark:bg-slate-900 border-slate-100 dark:border-slate-800'}`}>
          {isExample ? 'Ví dụ' : questionNumber}
        </span>
        <p className="font-bold text-slate-700 dark:text-slate-200 text-base leading-relaxed m-0">
          {sentence}
        </p>
      </div>
      <div className="shrink-0 md:ml-auto">
        <KetLetterInput
          question={question}
          letters={letters}
          answer={answer}
          onSelect={onSelect}
          showHint={showHint}
          isReviewMode={isReviewMode}
          isHydrated={isHydrated}
        />
      </div>
    </div>
  );
};

const KetReadingPart1 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      // 1. Prioritize API data in review mode
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      // 2. Otherwise load from localStorage
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData && typeof savedData === 'object') {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId || isReviewMode) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated, isReviewMode]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const { shared_options } = exerciseData.content_data;
  const letters = (shared_options || []).map(option => option.id);

  const sortedQuestions = [...exerciseData.questions].sort((a, b) => a.order_index - b.order_index);
  const exampleQuestion = sortedQuestions.find(q => q.points === 0);
  const mainQuestions = sortedQuestions.filter(q => q.points > 0);
  const numberMap = ketNumberMap(exerciseData);

  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

  const handleSelect = (questionId, letter) => {
    setAnswers(prev => ({ ...prev, [questionId]: letter }));
  };

  return (
    <div className="w-full h-full p-1 md:px-10 overflow-y-auto custom-scrollbar">
      <div className="max-w-4xl mx-auto w-full py-4 space-y-4">
        {/* The 8 lettered sign images */}
        {shared_options && shared_options.length > 0 && (
          <div className="bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-gray-200 dark:border-slate-600 p-4 shadow-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {shared_options.map(option => (
                <div key={option.id} className="flex flex-col items-center gap-2 p-2 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border-2 border-gray-200 dark:border-slate-700">
                  <span className="font-display font-black text-lg text-amber-600 bg-amber-50 dark:bg-amber-900/30 w-8 h-8 rounded-full flex items-center justify-center border border-gray-200">{option.id}</span>
                  {option.image_url && (
                    <img
                      src={`${ASSET_BASE_URL}${option.image_url}`}
                      alt={option.image_text || `Biển báo ${option.id}`}
                      className="w-full h-auto object-contain rounded-xl"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sentences with A–H picker */}
        <div className="space-y-3">
          {exampleQuestion && (
            <SentenceRow
              question={exampleQuestion}
              letters={letters}
              answer={answers[exampleQuestion.id]}
              onSelect={handleSelect}
              isReviewMode={isReviewMode}
              isHydrated={isHydrated}
            />
          )}
          {mainQuestions.map((q) => (
            <SentenceRow
              key={q.id}
              question={q}
              questionNumber={numberMap[q.id]}
              letters={letters}
              answer={answers[q.id]}
              onSelect={handleSelect}
              showHint={showHint}
              isReviewMode={isReviewMode}
              isHydrated={isHydrated}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default KetReadingPart1;
